import React, { useContext, useRef, useState, useEffect } from "react";

import {
  FaHeart,
  FaShoppingCart,
  FaUser,
  FaCog,
  FaBoxOpen,
  FaSignOutAlt,
  FaBell,
  FaTicketAlt,
  FaBars,
  FaGithub,
  FaTimes,
} from "react-icons/fa";

import { Link, useNavigate } from "react-router-dom";

import "../../../assets/css/user/Navbar.css";
import logo from "../../../assets/images/logo.png";

import AppContext from "../../../context/AppContext";
import axios from "axios";

const MyNavbar = () => {
  const { user, isLogin, setIsLogin, setUser, baseUrl } =
    useContext(AppContext);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  const [cartCount, setCartCount] = useState(0);

  const [wishlistCount, setWishlistCount] = useState(0);

  // =====================================================
  // CUSTOMER INSTANT DEAL REMINDER
  // =====================================================

  const [instantDeal, setInstantDeal] = useState(null);

  const [remainingTime, setRemainingTime] = useState(0);

  const dropdownRef = useRef(null);

  const navigate = useNavigate();

  // =====================================================
  // GET LOGGED-IN CUSTOMER
  // =====================================================

  const getLoggedInCustomer = () => {
    try {
      const savedCustomer = JSON.parse(localStorage.getItem("loggedInUser"));

      return savedCustomer;
    } catch (error) {
      console.log("Logged in customer parse error:", error);

      return null;
    }
  };

  // =====================================================
  // FETCH CART + WISHLIST COUNTS
  // =====================================================

  const fetchCounts = async () => {
    const customer = getLoggedInCustomer();

    if (!customer?.id) {
      setCartCount(0);
      setWishlistCount(0);
      setNotificationCount(0);
      return;
    }

    try {
      const cartFormData = new FormData();
      cartFormData.append("tpcustomer_id", customer.id);

      const wishlistFormData = new FormData();
      wishlistFormData.append("tpcustomer_id", customer.id);

      const notificationFormData = new FormData();
      notificationFormData.append("tpcustomer_id", customer.id);

      const [cartRes, wishlistRes, notificationRes] = await Promise.all([
        axios.post(`${baseUrl}/customer/cart/getCart`, cartFormData),
        axios.post(
          `${baseUrl}/customer/wishlist/viewWishlist`,
          wishlistFormData,
        ),
        axios.post(
          `${baseUrl}/customer/notification/viewAllNotification`,
          notificationFormData,
        ),
      ]);

      setCartCount(cartRes.data?.deals?.length || 0);
      setWishlistCount(wishlistRes.data?.wishlist?.length || 0);

      // Navbar mein sirf UNREAD notifications ka count show hoga.
      const notifications = notificationRes.data?.notifications || [];

      const unreadCount = notifications.filter(
        (item) => String(item?.status || "").toLowerCase() === "unread",
      ).length;

      setNotificationCount(unreadCount);
    } catch (error) {
      console.error(
        "Error fetching navbar counts:",
        error.response?.data || error,
      );
    }
  };

  // =====================================================
  // LOGIN / NOTIFICATION UPDATES
  // =====================================================

  const handleLoginUpdate = () => {
    fetchCounts();
    fetchCustomerInstantDeal();
  };

  const handleNotificationUpdate = () => {
    fetchCounts();
    fetchCustomerInstantDeal();
  };

  const fetchCustomerInstantDeal = () => {
    if (!isLogin) {
      setInstantDeal(null);
      setRemainingTime(0);
      return;
    }

    const savedCustomer = getLoggedInCustomer();

    if (!savedCustomer?.id) {
      setInstantDeal(null);
      setRemainingTime(0);
      return;
    }

    const formData = new FormData();

    formData.append("tpcustomer_id", savedCustomer.id);

    axios
      .post(`${baseUrl}/customer/orders/fetchCustomerOrders`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((res) => {
        console.log("Customer Orders for Reminder:", res.data);

        if (!res.data?.success) {
          setInstantDeal(null);
          setRemainingTime(0);
          return;
        }

        const orders = Array.isArray(res.data.orders) ? res.data.orders : [];

        // =================================================
        // GET ONLY PAID ORDERS
        // =================================================

        const paidOrders = orders.filter(
          (order) => order?.payment_status === "paid",
        );

        // =================================================
        // GET ONLY INSTANT DEAL ORDERS
        // =================================================
        //
        // Order ke tpcarts ke andar deal hai.
        //
        // =================================================

        const instantOrders = [];

        paidOrders.forEach((order) => {
          const carts = Array.isArray(order?.tpcarts) ? order.tpcarts : [];

          carts.forEach((cart) => {
            const deal = cart?.tpdeal;

            if (deal && deal.deal_type === "Instant Deals" && deal.start_at) {
              instantOrders.push({
                ...deal,
                order_id: order.id,
              });
            }
          });
        });

        // =================================================
        // IF NO INSTANT DEAL ORDER
        // =================================================

        if (instantOrders.length === 0) {
          setInstantDeal(null);
          setRemainingTime(0);
          return;
        }

        const now = new Date();

        // =================================================
        // ONLY UPCOMING INSTANT DEALS
        // =================================================

        const upcomingDeals = instantOrders
          .filter((deal) => {
            const startAt = new Date(deal.start_at);

            return !isNaN(startAt.getTime()) && startAt > now;
          })
          .sort((a, b) => {
            return new Date(a.start_at) - new Date(b.start_at);
          });

        // =================================================
        // SET NEXT CUSTOMER INSTANT DEAL
        // =================================================

        if (upcomingDeals.length > 0) {
          setInstantDeal(upcomingDeals[0]);
        } else {
          setInstantDeal(null);
          setRemainingTime(0);
        }
      })
      .catch((error) => {
        console.log(
          "Customer instant reminder error:",
          error.response?.data || error,
        );

        setInstantDeal(null);
        setRemainingTime(0);
      });
  };

  // =====================================================
  // INITIAL FETCH
  // =====================================================

  useEffect(() => {
    fetchCounts();
    fetchCustomerInstantDeal();

    window.addEventListener("loginUpdated", handleLoginUpdate);
    window.addEventListener("cartUpdated", fetchCounts);
    window.addEventListener("wishlistUpdated", fetchCounts);
    window.addEventListener("orderUpdated", fetchCustomerInstantDeal);
    window.addEventListener("notificationUpdated", handleNotificationUpdate);

    return () => {
      window.removeEventListener("loginUpdated", handleLoginUpdate);
      window.removeEventListener("cartUpdated", fetchCounts);
      window.removeEventListener("wishlistUpdated", fetchCounts);
      window.removeEventListener("orderUpdated", fetchCustomerInstantDeal);
      window.removeEventListener(
        "notificationUpdated",
        handleNotificationUpdate,
      );
    };
  }, [isLogin, baseUrl]);

  // =====================================================
  // REFRESH CUSTOMER ORDER REMINDER
  // =====================================================

  useEffect(() => {
    if (!isLogin) {
      return;
    }

    const interval = setInterval(() => {
      fetchCustomerInstantDeal();
    }, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [isLogin, baseUrl]);

  // =====================================================
  // INSTANT DEAL COUNTDOWN
  // =====================================================

  useEffect(() => {
    if (!instantDeal?.start_at) {
      setRemainingTime(0);
      return;
    }

    const calculateRemaining = () => {
      const startAt = new Date(instantDeal.start_at).getTime();

      const now = Date.now();

      const remaining = startAt - now;

      if (remaining <= 0) {
        setRemainingTime(0);

        // Deal start ho gayi.
        // Customer ka next upcoming instant deal check karo.
        fetchCustomerInstantDeal();

        return;
      }

      setRemainingTime(remaining);
    };

    calculateRemaining();

    const timer = setInterval(calculateRemaining, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [instantDeal]);

  // =====================================================
  // FORMAT COUNTDOWN
  // =====================================================

  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) {
      return "00:00";
    }

    const totalSeconds = Math.floor(milliseconds / 1000);

    const hours = Math.floor(totalSeconds / 3600);

    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0",
      )}:${String(seconds).padStart(2, "0")}`;
    }

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  };

  // =====================================================
  // CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  // =====================================================

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout = () => {
    setIsDropdownOpen(false);

    localStorage.removeItem("userEmail");

    localStorage.removeItem("loggedInUser");

    localStorage.removeItem("authToken");

    setIsLogin(false);

    setUser(null);

    setCartCount(0);
    setWishlistCount(0);
    setNotificationCount(0);

    setInstantDeal(null);
    setRemainingTime(0);

    navigate("/login");
  };

  // =====================================================
  // NAVIGATION LINKS
  // =====================================================

  const navLinks = [
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Deals",
      path: "/deals",
    },
    {
      name: "Shops",
      path: "/shop",
    },
    {
      name: "Blogs",
      path: "/blog",
    },
    {
      name: "Contact",
      path: "/contact-us",
    },
    {
      name: "About",
      path: "/about-us",
    },
  ];

  // =====================================================
  // INSTANT REMINDER BUTTON
  // =====================================================

  const InstantReminder = () => {
    if (!isLogin || !instantDeal || remainingTime <= 0) {
      return null;
    }

    return (
      <button
        className="mnb-instant-loader"
        onClick={() => navigate("/customer-orders")}
        title={
          instantDeal.deal_title
            ? `Instant Deal: ${instantDeal.deal_title}`
            : "Instant Deal Reminder"
        }
      >
        <div className="mnb-loader-ring">
          <svg viewBox="0 0 44 44">
            <circle className="mnb-loader-bg" cx="22" cy="22" r="19" />

            <circle className="mnb-loader-progress" cx="22" cy="22" r="19" />
          </svg>

          <div className="mnb-loader-icon">
            <FaGithub />
          </div>

          <span className="mnb-loader-time">{formatTime(remainingTime)}</span>
        </div>
      </button>
    );
  };

  // =====================================================
  // JSX
  // =====================================================

  return (
    <header className="mnb-navbar">
      {/* =================================================
          LOGO
      ================================================= */}

      <div className="mnb-navbar-left">
        <Link to="/" className="mnb-navbar-logo">
          <img src={logo} alt="Time4Pulse Logo" className="mnb-logo-icon" />
        </Link>
      </div>

      {/* =================================================
          HAMBURGER
      ================================================= */}

      <button
        className="mnb-hamburger"
        onClick={() => setIsMobileMenuOpen((prev) => !prev)}
        aria-label="Toggle menu"
      >
        {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
      </button>

      {/* =================================================
          NAV LINKS
      ================================================= */}

      <ul className={`mnb-navbar-links ${isMobileMenuOpen ? "mnb-open" : ""}`}>
        {navLinks.map((link) => (
          <li key={link.name}>
            <Link to={link.path} onClick={() => setIsMobileMenuOpen(false)}>
              {link.name}
            </Link>
          </li>
        ))}

        {/* =================================================
            MOBILE ACTIONS
        ================================================= */}

        <div className="mnb-mobile-actions">
          {isLogin ? (
            <>
              {/* Mobile Instant Reminder */}

              <InstantReminder />

              {/* Mobile Orders */}

              <button
                className="mnb-t4pdropdown-item"
                onClick={() => {
                  setIsMobileMenuOpen(false);

                  navigate("/customer-orders");
                }}
              >
                <FaBoxOpen className="mnb-dropdown-icon" />

                <span>Orders</span>
              </button>

              {/* Mobile Logout */}

              <button
                className="mnb-t4pdropdown-item mnb-dropdown-logout"
                onClick={handleLogout}
              >
                <FaSignOutAlt className="mnb-dropdown-icon" />

                <span>Logout</span>
              </button>
            </>
          ) : (
            <>
              <button
                className="mnb-btn-login"
                onClick={() => navigate("/login")}
              >
                Log In
              </button>

              <button
                className="mnb-sign-in-btn"
                onClick={() => navigate("/signup")}
              >
                Sign Up
              </button>
            </>
          )}
        </div>
      </ul>

      {/* =================================================
          RIGHT NAVBAR ACTIONS
      ================================================= */}

      <div className="mnb-navbar-actions">
        {/* =================================================
            CUSTOMER'S INSTANT DEAL REMINDER
        ================================================= */}

        <InstantReminder />

        {/* =================================================
            LOGIN USER ACTIONS
        ================================================= */}

        {isLogin && (
          <>
            {/* Notification */}

            <button
              className="mnb-action-btn mnb-nav-icon-btn"
              onClick={() => navigate("/notification-page")}
              title="Notifications"
            >
              <FaBell />

              {notificationCount > 0 && (
                <span className="mnb-nav-badge">{notificationCount}</span>
              )}
            </button>

            {/* Wishlist */}

            <button
              className="mnb-action-btn mnb-nav-icon-btn"
              onClick={() => navigate("/wishlist")}
              title="Wishlist"
            >
              <FaHeart />

              {wishlistCount > 0 && (
                <span className="mnb-nav-badge">{wishlistCount}</span>
              )}
            </button>

            {/* Cart */}

            <button
              className="mnb-action-btn mnb-nav-icon-btn"
              onClick={() => navigate("/cart")}
              title="Cart"
            >
              <FaShoppingCart />

              {cartCount > 0 && (
                <span className="mnb-nav-badge">{cartCount}</span>
              )}
            </button>
          </>
        )}

        {/* =================================================
            PROFILE / LOGIN
        ================================================= */}

        {isLogin ? (
          <div className="mnb-profile-wrapper" ref={dropdownRef}>
            {/* Profile Button */}

            <button
              className="mnb-profile-btn"
              onClick={() => setIsDropdownOpen((prev) => !prev)}
            >
              {user?.image ? (
                <img
                  src={user.image}
                  alt="profile"
                  className="mnb-profile-img"
                />
              ) : (
                <FaUser />
              )}
            </button>

            {/* Profile Dropdown */}

            {isDropdownOpen && (
              <div className="mnb-profile-dropdown">
                {/* User Info */}

                {user?.name && (
                  <div className="mnb-dropdown-header">
                    <span className="mnb-dropdown-username">{user.name}</span>

                    {user?.email && (
                      <span className="mnb-dropdown-email">{user.email}</span>
                    )}
                  </div>
                )}

                {/* Settings */}

                <button
                  className="mnb-t4pdropdown-item"
                  onClick={() => {
                    setIsDropdownOpen(false);

                    navigate("/customer-setting");
                  }}
                >
                  <FaCog className="mnb-dropdown-icon" />

                  <span>Settings</span>
                </button>

                <div className="mnb-dropdown-divider" />

                {/* Orders */}

                <button
                  className="mnb-t4pdropdown-item"
                  onClick={() => {
                    setIsDropdownOpen(false);

                    navigate("/customer-orders");
                  }}
                >
                  <FaBoxOpen className="mnb-dropdown-icon" />

                  <span>Orders</span>
                </button>

                {/* Support Tickets */}

                <button
                  className="mnb-t4pdropdown-item"
                  onClick={() => {
                    setIsDropdownOpen(false);

                    navigate("/support-tickets");
                  }}
                >
                  <FaTicketAlt className="mnb-dropdown-icon" />

                  <span>Support Tickets</span>
                </button>

                {/* Logout */}

                <button
                  className="mnb-t4pdropdown-item mnb-dropdown-logout"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt className="mnb-dropdown-icon" />

                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="mnb-desktop-auth-btns">
            {/* Login */}

            <button
              className="mnb-btn-login"
              onClick={() => navigate("/login")}
            >
              <span>Log In</span>
            </button>

            {/* Signup */}

            <button
              className="mnb-sign-in-btn"
              onClick={() => navigate("/signup")}
            >
              <span>Sign Up</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default MyNavbar;
